import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import apiClient from "@shared/services/apiClient";
import type { ConsensusResolutionMethod } from "../components/eegViewerChromeShared";
import {
  EMPTY_CONSENSUS_DISAGREEMENTS,
  toUserFacingApiError,
  type ConsensusAutoSummary,
  type ConsensusDisagreementSummary,
  type ConsensusReportSummary,
  type ConsensusReviewerAssignmentResponse,
  type ConsensusReviewerSearchResult,
} from "../types/eegViewerAnnotationTypes";

/**
 * Consensus review workflow extracted from EEGViewer to keep the main
 * component focused on rendering. Owns consensus UI state (reviewer
 * search/selection, mode toggles, blind-review + resolution-method
 * preferences, action status/error), the consensus data queries + derived
 * disagreement/assignment memos, the assign-reviewers/resolve-disagreements
 * mutations, and the handlers wired to the consensus review panel.
 */

export interface UseEEGConsensusReviewParams {
  studyId: string;
  clinicalAnnotationPanelVisible: boolean;
}

export function useEEGConsensusReview(params: UseEEGConsensusReviewParams) {
  const { studyId, clinicalAnnotationPanelVisible } = params;

  const [consensusReviewerSearch, setConsensusReviewerSearch] =
    useState<string>("");
  const [consensusSelectedReviewers, setConsensusSelectedReviewers] = useState<
    ConsensusReviewerSearchResult[]
  >([]);
  const [consensusModeEnabled, setConsensusModeEnabled] =
    useState<boolean>(false);
  const [consensusSectionOpen, setConsensusSectionOpen] =
    useState<boolean>(false);
  const [consensusBlindReviewMode, setConsensusBlindReviewMode] =
    useState<boolean>(false);
  const [consensusResolutionMethod, setConsensusResolutionMethod] =
    useState<ConsensusResolutionMethod>("majority");
  const [consensusActionStatus, setConsensusActionStatus] = useState<
    string | null
  >(null);
  const [consensusActionError, setConsensusActionError] = useState<
    string | null
  >(null);

  const {
    data: consensusAutoSummary = null,
    refetch: refetchConsensusAutoSummary,
    isFetching: isFetchingConsensusAutoSummary,
  } = useQuery({
    queryKey: ["eeg-consensus-auto-summary", studyId],
    queryFn: async () => {
      try {
        const response = await apiClient.get(
          `/consensus/auto-calculate/${studyId}`,
        );
        const payload = response?.data?.data ?? response?.data ?? null;
        return payload as ConsensusAutoSummary | null;
      } catch (error) {
        const normalized = toUserFacingApiError(error);
        if (normalized.status === 400) {
          return null;
        }
        throw error;
      }
    },
    enabled: Boolean(studyId) && clinicalAnnotationPanelVisible,
    staleTime: 30_000,
    initialData: null,
    refetchOnWindowFocus: false,
  });

  const {
    data: consensusReportSummary = null,
    refetch: refetchConsensusReportSummary,
    isFetching: isFetchingConsensusReportSummary,
  } = useQuery({
    queryKey: ["eeg-consensus-report-summary", studyId],
    queryFn: async () => {
      const response = await apiClient.get(`/consensus/report/${studyId}`);
      const payload = response?.data?.data ?? response?.data ?? null;
      return payload as ConsensusReportSummary | null;
    },
    enabled: Boolean(studyId) && clinicalAnnotationPanelVisible,
    staleTime: 30_000,
    initialData: null,
    refetchOnWindowFocus: false,
  });

  const {
    data: consensusDisagreementSummary = null,
    refetch: refetchConsensusDisagreementSummary,
    isFetching: isFetchingConsensusDisagreementSummary,
  } = useQuery({
    queryKey: ["eeg-consensus-disagreements", studyId],
    queryFn: async () => {
      const response = await apiClient.get(
        `/consensus/disagreements/${studyId}`,
      );
      const payload = response?.data?.data ?? response?.data ?? null;
      return payload as ConsensusDisagreementSummary | null;
    },
    enabled: Boolean(studyId) && clinicalAnnotationPanelVisible,
    staleTime: 30_000,
    initialData: null,
    refetchOnWindowFocus: false,
  });

  const { data: consensusReviewerSearchResults = [] } = useQuery({
    queryKey: ["eeg-consensus-reviewer-search", consensusReviewerSearch],
    queryFn: async () => {
      const response = await apiClient.get("/users/search", {
        params: {
          q: consensusReviewerSearch.trim(),
          limit: 12,
        },
      });
      const payload = response?.data;
      const list: ConsensusReviewerSearchResult[] = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload)
          ? payload
          : [];
      return list.filter(
        (item) => typeof item?.user_id === "string" && item.user_id.length > 0,
      );
    },
    enabled:
      clinicalAnnotationPanelVisible && consensusReviewerSearch.trim().length >= 2,
    staleTime: 15_000,
    initialData: [],
    refetchOnWindowFocus: false,
  });

  const {
    data: consensusAssignmentSummary = null,
    refetch: refetchConsensusAssignmentSummary,
    isFetching: isFetchingConsensusAssignmentSummary,
  } = useQuery({
    queryKey: ["eeg-consensus-reviewer-assignments", studyId],
    queryFn: async () => {
      const response = await apiClient.get(
        `/consensus/review-assignments/${studyId}`,
      );
      const payload = response?.data?.data ?? response?.data ?? null;
      return payload as ConsensusReviewerAssignmentResponse | null;
    },
    enabled: Boolean(studyId) && clinicalAnnotationPanelVisible,
    staleTime: 15_000,
    initialData: null,
    refetchOnWindowFocus: false,
  });

  const consensusAssignedReviewers = useMemo(
    () => consensusAssignmentSummary?.assignments || [],
    [consensusAssignmentSummary?.assignments],
  );

  const assignedConsensusReviewerIdSet = useMemo(
    () =>
      new Set(consensusAssignedReviewers.map((reviewer) => reviewer.user_id)),
    [consensusAssignedReviewers],
  );

  useEffect(() => {
    if (typeof consensusAssignmentSummary?.blindReview === "boolean") {
      setConsensusBlindReviewMode(consensusAssignmentSummary.blindReview);
    }
  }, [consensusAssignmentSummary?.blindReview, studyId]);

  const consensusDisagreements = useMemo(
    () =>
      consensusDisagreementSummary?.disagreements ||
      consensusReportSummary?.disagreements ||
      EMPTY_CONSENSUS_DISAGREEMENTS,
    [
      consensusDisagreementSummary?.disagreements,
      consensusReportSummary?.disagreements,
    ],
  );

  const unresolvedConsensusDisagreements = useMemo(
    () =>
      consensusDisagreements.filter(
        (item) => !item.resolvedBy && !item.finalDecision,
      ),
    [consensusDisagreements],
  );

  const consensusReviewerCount = useMemo(() => {
    const assignedCount = Number(
      consensusAssignmentSummary?.reviewerCount || 0,
    );
    if (assignedCount > 0) return assignedCount;

    const summaryCount = Number(consensusAutoSummary?.reviewerCount || 0);
    if (summaryCount > 0) return summaryCount;

    const inferredReviewerIds = new Set<string>();
    consensusDisagreements.forEach((item) => {
      (item.reviewers || []).forEach((reviewer) => {
        if (reviewer?.reviewerId) {
          inferredReviewerIds.add(reviewer.reviewerId);
        }
      });
    });
    return inferredReviewerIds.size;
  }, [
    consensusAssignmentSummary?.reviewerCount,
    consensusAutoSummary?.reviewerCount,
    consensusDisagreements,
  ]);

  const hasPersistedConsensusAssignments =
    (consensusAssignmentSummary?.reviewerCount || 0) > 0;

  const hasConsensusReviewerCoverage = consensusReviewerCount > 1;
  const hasConsensusDisagreementData =
    unresolvedConsensusDisagreements.length > 0 ||
    Number(consensusDisagreementSummary?.totalDisagreements || 0) > 0 ||
    (consensusReportSummary?.disagreements?.length || 0) > 0;
  const shouldShowConsensusWorkflow =
    Boolean(studyId) &&
    (hasPersistedConsensusAssignments ||
      hasConsensusReviewerCoverage ||
      hasConsensusDisagreementData ||
      consensusModeEnabled);
  const canDisableManualConsensusMode =
    consensusModeEnabled &&
    !hasConsensusReviewerCoverage &&
    !hasConsensusDisagreementData;

  useEffect(() => {
    setConsensusModeEnabled(false);
    setConsensusSectionOpen(false);
  }, [studyId]);

  const selectedConsensusReviewerIdSet = useMemo(
    () =>
      new Set(consensusSelectedReviewers.map((reviewer) => reviewer.user_id)),
    [consensusSelectedReviewers],
  );

  const availableConsensusReviewerResults = useMemo(
    () =>
      consensusReviewerSearchResults.filter(
        (reviewer) =>
          !selectedConsensusReviewerIdSet.has(reviewer.user_id) &&
          !assignedConsensusReviewerIdSet.has(reviewer.user_id),
      ),
    [
      assignedConsensusReviewerIdSet,
      consensusReviewerSearchResults,
      selectedConsensusReviewerIdSet,
    ],
  );

  const assignConsensusReviewersMutation = useMutation({
    mutationFn: async (payload: {
      reviewerIds: string[];
      blindReview: boolean;
    }) => {
      const response = await apiClient.post("/consensus/assign-reviewers", {
        studyId,
        reviewerIds: payload.reviewerIds,
        blindReview: payload.blindReview,
      });
      return response?.data?.data ?? response?.data ?? null;
    },
    onSuccess: async (_data, variables) => {
      setConsensusActionError(null);
      setConsensusActionStatus(
        `${variables.reviewerIds.length} reviewer${variables.reviewerIds.length === 1 ? "" : "s"} assigned (${variables.blindReview ? "blind" : "non-blind"} mode).`,
      );
      setConsensusReviewerSearch("");
      setConsensusSelectedReviewers([]);
      await refetchConsensusAssignmentSummary();
      await refetchConsensusAutoSummary();
      await refetchConsensusReportSummary();
      await refetchConsensusDisagreementSummary();
    },
    onError: (error) => {
      const normalized = toUserFacingApiError(error);
      setConsensusActionStatus(null);
      setConsensusActionError(normalized.message);
    },
  });

  const resolveConsensusDisagreementsMutation = useMutation({
    mutationFn: async (resolutionMethod: ConsensusResolutionMethod) => {
      const response = await apiClient.post(`/consensus/resolve/${studyId}`, {
        resolutionMethod,
      });
      return (response?.data?.data ?? response?.data ?? null) as {
        resolvedCount?: number;
        failedCount?: number;
      } | null;
    },
    onSuccess: async (data, resolutionMethod) => {
      const resolved = Number(data?.resolvedCount || 0);
      const failed = Number(data?.failedCount || 0);
      setConsensusActionError(null);
      setConsensusActionStatus(
        `Resolution (${resolutionMethod}) complete: ${resolved} resolved, ${failed} pending manual review.`,
      );
      await refetchConsensusAutoSummary();
      await refetchConsensusReportSummary();
      await refetchConsensusDisagreementSummary();
    },
    onError: (error) => {
      const normalized = toUserFacingApiError(error);
      setConsensusActionStatus(null);
      setConsensusActionError(normalized.message);
    },
  });

  const consensusActionPending =
    assignConsensusReviewersMutation.isPending ||
    resolveConsensusDisagreementsMutation.isPending;

  const refreshConsensusData = useCallback(async () => {
    setConsensusActionError(null);
    setConsensusActionStatus(null);
    await refetchConsensusAssignmentSummary();
    await refetchConsensusAutoSummary();
    await refetchConsensusReportSummary();
    await refetchConsensusDisagreementSummary();
  }, [
    refetchConsensusAssignmentSummary,
    refetchConsensusAutoSummary,
    refetchConsensusDisagreementSummary,
    refetchConsensusReportSummary,
  ]);

  const addConsensusReviewer = useCallback(
    (reviewer: ConsensusReviewerSearchResult) => {
      setConsensusSelectedReviewers((current) =>
        current.some((item) => item.user_id === reviewer.user_id)
          ? current
          : [...current, reviewer],
      );
      setConsensusActionError(null);
      setConsensusActionStatus(null);
    },
    [],
  );

  const removeConsensusReviewer = useCallback((reviewerId: string) => {
    setConsensusSelectedReviewers((current) =>
      current.filter((item) => item.user_id !== reviewerId),
    );
  }, []);

  const handleAssignConsensusReviewers = useCallback(() => {
    const reviewerIds = consensusSelectedReviewers.map(
      (reviewer) => reviewer.user_id,
    );
    if (reviewerIds.length === 0) {
      setConsensusActionStatus(null);
      setConsensusActionError(
        "Select one or more reviewers from search results.",
      );
      return;
    }

    setConsensusActionStatus(null);
    setConsensusActionError(null);
    assignConsensusReviewersMutation.mutate({
      reviewerIds,
      blindReview: consensusBlindReviewMode,
    });
  }, [
    assignConsensusReviewersMutation,
    consensusBlindReviewMode,
    consensusSelectedReviewers,
  ]);

  const handleEnableConsensusMode = useCallback(() => {
    setConsensusModeEnabled(true);
    setConsensusSectionOpen(true);
    setConsensusActionError(null);
    setConsensusActionStatus(null);
  }, []);

  const handleDisableConsensusMode = useCallback(() => {
    setConsensusModeEnabled(false);
    setConsensusSectionOpen(false);
    setConsensusReviewerSearch("");
    setConsensusSelectedReviewers([]);
    setConsensusBlindReviewMode(false);
    setConsensusActionError(null);
    setConsensusActionStatus(null);
  }, []);

  const handleToggleConsensusSection = useCallback(() => {
    setConsensusSectionOpen((current) => !current);
  }, []);

  const handleResolveConsensusDisagreements = useCallback(() => {
    setConsensusActionStatus(null);
    setConsensusActionError(null);
    resolveConsensusDisagreementsMutation.mutate(consensusResolutionMethod);
  }, [consensusResolutionMethod, resolveConsensusDisagreementsMutation]);

  return {
    // UI state
    consensusReviewerSearch,
    setConsensusReviewerSearch,
    consensusSelectedReviewers,
    consensusModeEnabled,
    consensusSectionOpen,
    consensusBlindReviewMode,
    setConsensusBlindReviewMode,
    consensusResolutionMethod,
    setConsensusResolutionMethod,
    consensusActionStatus,
    consensusActionError,

    // queries / derived data
    consensusAutoSummary,
    isFetchingConsensusAutoSummary,
    consensusReportSummary,
    isFetchingConsensusReportSummary,
    consensusDisagreementSummary,
    isFetchingConsensusDisagreementSummary,
    consensusReviewerSearchResults,
    consensusAssignmentSummary,
    isFetchingConsensusAssignmentSummary,
    consensusAssignedReviewers,
    consensusDisagreements,
    unresolvedConsensusDisagreements,
    consensusReviewerCount,
    hasPersistedConsensusAssignments,
    hasConsensusReviewerCoverage,
    hasConsensusDisagreementData,
    shouldShowConsensusWorkflow,
    canDisableManualConsensusMode,
    availableConsensusReviewerResults,

    // mutations
    assignConsensusReviewersMutation,
    resolveConsensusDisagreementsMutation,
    consensusActionPending,

    // handlers
    refreshConsensusData,
    addConsensusReviewer,
    removeConsensusReviewer,
    handleAssignConsensusReviewers,
    handleEnableConsensusMode,
    handleDisableConsensusMode,
    handleToggleConsensusSection,
    handleResolveConsensusDisagreements,
  };
}

export type UseEEGConsensusReviewResult = ReturnType<
  typeof useEEGConsensusReview
>;
